import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { QuickOnboardingTool, QuickOnboardingProps } from './types';
import { Image } from '../../ui/Image';
import { useQuickOnboardingConfig } from './useQuickOnboardingConfig';

import { useTranslation } from 'react-i18next';

export const QuickOnboardingCard: React.FC<{
  tool: QuickOnboardingTool;
  onConnect: () => void;
  isCompact: boolean;
  onDismiss: () => void;
}> = ({ tool, onConnect, onDismiss }) => {
  const { t } = useTranslation('pleri/shared');
  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-orange-200 bg-white p-4 shadow-xl shadow-orange-50/50 transition-all duration-300 dark:border-gray-600 dark:bg-gray-900">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-transparent to-transparent" />
      {/* Dismiss button */}
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 z-10 rounded-full p-1 text-gray-400 transition-all duration-200 hover:scale-110 hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-gray-400"
        aria-label={t('dismissName', { name: tool.name })}
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
      <div className="relative flex gap-3">
        {/* Icon column */}
        <div className="flex-shrink-0">
          <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-orange-50 to-orange-100 shadow-sm ring-1 ring-orange-300/60 transition-all duration-300">
            {tool.image ? (
              <Image
                {...tool.image}
                width={24}
                height={24}
                className="h-6 w-6 object-contain"
              />
            ) : tool.icon ? (
              typeof tool.icon === 'string' ? (
                <span className="text-lg">{tool.icon}</span>
              ) : (
                <div className="h-6 w-6">{tool.icon}</div>
              )
            ) : (
              <div className="h-6 w-6 rounded bg-gradient-to-br from-orange-200 to-orange-300" />
            )}
          </div>
        </div>

        {/* Content column */}
        <div className="min-w-0 flex-1">
          {/* Title */}
          <h3 className="text-sm font-semibold text-gray-800 transition-colors duration-200 dark:text-gray-200">
            {tool.name}
          </h3>

          {/* Description */}
          <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
            {tool.description}
          </p>

          {/* Connect button */}
        </div>
      </div>
      <div className="z-10 mt-auto ml-auto pt-2">
        <button
          onClick={onConnect}
          className="z-10 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2 text-xs font-medium text-white shadow-sm transition-all duration-200 hover:scale-105 hover:from-orange-600 hover:to-orange-700 hover:shadow-md hover:shadow-orange-200/50 active:scale-95"
        >
          {tool.buttonText}
        </button>
      </div>
    </div>
  );
};

export const QuickOnboarding: React.FC<QuickOnboardingProps> = ({
  className = '',
  onToolClick,
  isCompact = false,
}) => {
  const navigate = useNavigate();
  const { visibleTools, handleDismiss, isLoading, isError } =
    useQuickOnboardingConfig({
      isCompact,
    });

  const handleToolClick = (tool: QuickOnboardingTool) => {
    if (onToolClick) {
      onToolClick(tool);
    } else {
      // Default behavior: navigate to the tool's route
      navigate({ to: tool.route as string });
    }
  };

  // Don't render if there's an error or no tools are visible
  if (isError || visibleTools.length === 0 || isLoading) {
    return null;
  }

  return (
    <div className={`mt-4 space-y-4 ${className}`}>
      <div
        className={`grid gap-4 ${visibleTools.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}
      >
        {visibleTools.map((tool) => (
          <QuickOnboardingCard
            isCompact={isCompact}
            key={tool.id}
            tool={tool}
            onConnect={() => handleToolClick(tool)}
            onDismiss={() => handleDismiss(tool.id)}
          />
        ))}
      </div>
    </div>
  );
};
